import React from 'react'
import { isNil } from 'ramda'
import { maxFileSize as maxFileSizeUtil } from 'sidecar-utilities'

import { Icon, IconSize, IconType } from '@sidecar-components/atoms/Icon'
import {
  dropzoneStyles,
  errorStyles,
  iconStyles,
  inputStyles,
  labelStyles,
  titleStyles,
  topIconStyles,
} from '@sidecar-components/atoms/FileDropzone/styles'
import { FileDropzoneProps } from './FileDropzone.types'

/**
 * File dropzone component that allows users to upload multiple files via
 * clicking to open a file dialog or dragging and dropping files onto the
 * dropzone area
 *
 * @param {Record<string, string>} acceptedFileTypes - Object defining accepted
 *   file types and their corresponding extensions
 * @param {boolean} busy - Whether the dropzone is in a busy/loading state
 * @param {string} className - Additional class names for the dropzone
 * @param {string} testId - Test ID for testing purposes
 * @param {boolean} disabled - Whether the dropzone is disabled
 * @param {string} error - Error message to display below the dropzone
 * @param {string} maxFileSize - Maximum file size allowed for uploads
 * @param {string} name - Name attribute for the file input element
 * @param {function} handleChange - Callback function to handle file selection
 *   changes
 * @param {keyof typeof largeIcons} topIcon - Optional icon to display at the
 *   top of the dropzone
 * @returns {React.ReactNode} Input field that accepts multiple file uploads via
 *   click or drag and drop
 */
export const FileDropzone = ({
  acceptedFileTypes = {
    jpg: '.jpg, .jpeg',
    gif: '.gif',
    png: '.png',
    pdf: '.pdf',
  },
  busy,
  className,
  testId = '',
  disabled,
  error,
  maxFileSize = maxFileSizeUtil.toString,
  name,
  handleChange,
  topIcon,
}: FileDropzoneProps) => {
  return (
    <div
      className={`${dropzoneStyles({
        disabled,
        busy,
        hasTopIcon: !isNil(topIcon),
      })} ${className}`}
      data-testid={testId}
    >
      <input
        type="file"
        id={name}
        name={name}
        multiple
        autoComplete="off"
        onChange={handleChange}
        className={inputStyles}
        disabled={disabled}
        accept={Object.values(acceptedFileTypes).join(', ')}
        data-testid={`${testId}Input`}
      />

      {!isNil(topIcon) && (
        <Icon
          name={topIcon}
          size={IconSize.XXXL}
          type={IconType.LARGE}
          className={topIconStyles}
        />
      )}

      <Icon
        name={busy ? 'loading' : 'upload'}
        size={IconSize.MD}
        className={iconStyles({
          busy,
          hasTopIcon: !isNil(topIcon),
        })}
      />

      <div className="w-full">
        <label
          htmlFor={name}
          className={labelStyles({
            hasTopIcon: !isNil(topIcon),
          })}
        >
          <div className={titleStyles(disabled)}>
            Click <span className="hidden sm:inline">or drag files </span>here
            to upload
          </div>
          <div className="sh-body-sm">
            Accepts {Object.values(acceptedFileTypes).join(', ')} files
            {!!maxFileSize && ` (${maxFileSize} max)`}
          </div>
        </label>

        {error ? (
          <div className={errorStyles(!isNil(topIcon))}>{error}</div>
        ) : null}
      </div>
    </div>
  )
}
